<template>
  <div class="column full-width full-height no-wrap bg-white relative-position">
    <!-- TOP HEADER / INSTRUCTIONS -->
    <div class="q-pa-sm bg-primary text-white text-center shadow-2" style="z-index: 10">
      <div class="text-weight-bold text-subtitle1">Scouting & Evaluation</div>
      <div class="text-caption opacity-80">Tap fielders to select them for evaluation</div>
    </div>
    <!-- BOTTOM SHEET: TAGGING ACTIONS (Slides up when fielders are selected) -->
    <q-slide-transition>
      <q-card
        v-if="selectedPositions.length > 0"
        class="col-auto shadow-up-3 bg-white"
        style="z-index: 20; border-top-left-radius: 16px; border-top-right-radius: 16px"
      >
        <q-card-section class="q-pb-none">
          <div class="row items-center justify-between">
            <div class="text-subtitle2 text-weight-bold text-primary">
              Evaluating {{ selectedPositions.length }} Fielder(s)
            </div>
            <q-btn
              flat
              dense
              icon="clear_all"
              color="negative"
              label="Clear All"
              @click="clearSelection"
            />
          </div>
          <!-- Selected Fielders as Removable Chips -->
          <div class="row q-gutter-xs q-mb-md">
            <q-chip
              v-for="pos in selectedPositions"
              :key="'selected-' + pos"
              removable
              color="primary"
              text-color="white"
              dense
              @remove="toggleFielder(pos, $event)"
            >
              {{ settingsStore.getPositionShortLabel(pos) }}
            </q-chip>
          </div>
        </q-card-section>

        <q-card-section>
          <!-- QUICK TAGS -->
          <div class="text-caption text-weight-bold text-grey-8 q-mb-sm">Select Tag:</div>
          <div class="row q-gutter-sm q-mb-md">
            <q-btn
              v-for="tag in settingsStore.activeScoutingTags"
              :key="tag.value"
              :label="tag.label"
              :color="selectedTag === tag.value ? tag.color : 'grey-2'"
              :text-color="selectedTag === tag.value ? 'white' : 'grey-8'"
              unelevated
              rounded
              size="sm"
              class="text-weight-bold"
              @click="selectTag(tag.value)"
            />
          </div>

          <!-- CATEGORY (positive/neutral/negative) — shown once a tag is picked -->
          <div v-if="selectedTag" class="text-caption text-weight-bold text-grey-8 q-mb-sm">
            Was this:
          </div>
          <div v-if="selectedTag" class="row q-gutter-sm q-mb-md">
            <q-btn
              v-for="opt in categoryOptions"
              :key="opt.value"
              :label="opt.label"
              :color="selectedCategory === opt.value ? opt.color : 'grey-2'"
              :text-color="selectedCategory === opt.value ? 'white' : 'grey-8'"
              unelevated
              rounded
              size="sm"
              class="col text-weight-bold"
              @click="selectedCategory = opt.value"
            />
          </div>

          <!-- OPTIONAL CUSTOM NOTE -->
          <q-input
            v-model="customNote"
            label="Optional Note..."
            outlined
            dense
            autogrow
            class="q-mb-md"
          >
            <template v-slot:prepend>
              <q-icon name="edit_note" />
            </template>
          </q-input>

          <!-- ACTION BUTTONS -->
          <div class="row q-gutter-sm">
            <!-- ACTION BUTTONS — require category too -->
            <q-btn
              label="Save Evaluation"
              color="primary"
              icon="save"
              class="col text-weight-bold"
              unelevated
              :disable="!selectedTag || !selectedCategory"
              @click="saveEvaluation"
            />
          </div>
        </q-card-section>
      </q-card>
    </q-slide-transition>

    <!-- FIELD CANVAS -->
    <div
      ref="containerRef"
      class="canvas-container col-grow relative-position overflow-hidden bg-grey-2"
    >
      <v-stage
        class="absolute-top-left"
        v-if="stageConfig.width > 0"
        :config="{
          ...stageConfig,
          onClick: handleCanvasTap,
          onTap: handleCanvasTap,
        }"
      >
        <v-layer>
          <!-- Base Field Layer -->
          <v-rect :config="backgroundConfig" />
          <v-wedge :config="outfieldWedgeConfig" />
          <v-wedge :config="infieldDirtConfig" />
          <v-regular-polygon :config="diamondConfig" />
          <v-regular-polygon :config="homePlateConfig" />

          <v-line :config="retroLineLCConfig" />
          <v-line :config="retroLineRCConfig" />
          <v-arc :config="retroShortArcConfig" />

          <!-- DEFENSIVE FIELDERS -->
          <v-group
            v-for="fielder in fielders"
            :key="'eval-fielder-' + fielder.position"
            :config="{
              x: fielder.x,
              y: fielder.y,
              onClick: (e: any) => toggleFielder(fielder.position, e),
              onTap: (e: any) => toggleFielder(fielder.position, e),
            }"
          >
            <!-- Highlight Ring (Visible only if selected) -->
            <v-circle
              v-if="selectedPositions.includes(fielder.position)"
              :config="{
                radius: 22,
                fill: 'rgba(241, 196, 15, 0.4)',
                stroke: '#f1c40f',
                strokeWidth: 2,
              }"
            />

            <!-- Standard Player Badge -->
            <FieldPlayerMarker
              type="fielder"
              :uniform-number="fielder.uniformNumber"
              :position="fielder.position"
              :x="0"
              :y="0"
              :opacity="1"
              :show-zone="false"
              :show-position-initials="true"
            />
          </v-group>
        </v-layer>
      </v-stage>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue'
import { useQuasar } from 'quasar'
import { useGameStore } from 'stores/game-store'
import { useSettingsStore } from 'stores/settings-store'
import { useFieldLayout } from 'src/helpers/helpers'
import { db } from 'src/models/db'
import FieldPlayerMarker from 'components/FieldPlayerMarker.vue'
import type { Position, ScoutingTransaction } from 'src/stores/base-types'

const $q = useQuasar()
const gameStore = useGameStore()
const settingsStore = useSettingsStore()

// --- STATE ---
const selectedPositions = ref<number[]>([])
const selectedTag = ref<string | null>(null)
const selectedCategory = ref<'positive' | 'negative' | 'neutral' | null>(null)
const categoryOptions: {
  label: string
  value: 'positive' | 'negative' | 'neutral'
  color: string
}[] = [
  { label: 'Positive', value: 'positive', color: 'positive' },
  { label: 'Neutral', value: 'neutral', color: 'grey-7' },
  { label: 'Negative', value: 'negative', color: 'negative' },
]
const customNote = ref('')

// --- CANVAS SETUP ---
const containerRef = ref<HTMLElement | null>(null)
const stageConfig = reactive({ width: 0, height: 0 })
let resizeObserver: ResizeObserver | null = null

const stageSizeRef = computed(() => ({ width: stageConfig.width, height: stageConfig.height }))
const {
  backgroundConfig,
  outfieldWedgeConfig,
  infieldDirtConfig,
  diamondConfig,
  homePlateConfig,
  retroLineLCConfig,
  retroLineRCConfig,
  retroShortArcConfig,
  getStandardPositionCoords,
} = useFieldLayout(stageSizeRef)
onMounted(() => {
  if (containerRef.value) {
    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        stageConfig.width = entry.contentRect.width
        stageConfig.height = entry.contentRect.height
      }
    })
    resizeObserver.observe(containerRef.value)
  }
})

onBeforeUnmount(() => {
  if (resizeObserver && containerRef.value) resizeObserver.unobserve(containerRef.value)
})

// --- DATA MAPPING ---
// Grab the active defense to plot on the field
const fielders = computed(() => {
  const arr = []
  for (const fielder of gameStore.currentDefence) {
    if (fielder.position >= 1 && fielder.position <= 9) {
      const coords = getStandardPositionCoords(fielder.position)
      arr.push({
        position: fielder.position,
        uniformNumber: fielder.uniformNumber,
        x: coords.x,
        y: coords.y,
      })
    }
  }
  return arr
})

// --- INTERACTIONS ---
const selectTag = (tagValue: string) => {
  selectedTag.value = tagValue
  // Seed the category from the tag's own default, but only if the coach hasn't
  // already made an explicit choice this round — don't clobber an override.
  if (selectedCategory.value === null) {
    const tagDef = settingsStore.activeScoutingTags.find((t) => t.value === tagValue)
    selectedCategory.value = tagDef?.category ?? 'neutral'
  }
}
// clearSelection — reset the new field too
const clearSelection = () => {
  selectedPositions.value = []
  selectedTag.value = null
  selectedCategory.value = null
  customNote.value = ''
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const toggleFielder = (position: number, e: any) => {
  // Prevent the canvas from clearing the selection
  if (e && e.cancelBubble !== undefined) e.cancelBubble = true

  const index = selectedPositions.value.indexOf(position)
  if (index > -1) {
    selectedPositions.value.splice(index, 1)
  } else {
    selectedPositions.value.push(position)
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleCanvasTap = (e: any) => {
  // Get the specific Konva shape the user's finger actually touched
  const nodeType = e.target.getClassName()

  // If the user tapped a fielder badge (Circle or Text), ignore the canvas clear!
  if (nodeType === 'Circle' || nodeType === 'Text') {
    return
  }

  // If they tapped the grass (Rect, Wedge, Line, etc.), safely clear the selection
  //clearSelection()
}

// saveEvaluation — use the explicit category, not the tag's baked-in one
const saveEvaluation = async () => {
  if (!selectedTag.value || !selectedCategory.value || selectedPositions.value.length === 0) return
  if (!gameStore.activeGameSummary) return

  const currentPA = gameStore.viewedPlateAppearance
  const snapshot = gameStore.currentDefenseSnapshot

  const targetPlayerIds = selectedPositions.value
    .map((pos) => {
      const state = snapshot[pos as Position]
      // Safely extract the ID whether it's the old raw number format or the new object format
      return typeof state === 'number' ? state : state?.playerId
    })
    .filter((id): id is number => id !== undefined)

  const tagDef = settingsStore.activeScoutingTags.find((t) => t.value === selectedTag.value)

  const pitchTxns = gameStore.currentAtBatTransactions || []
  const activePitchId = gameStore.editingPitchId || pitchTxns[pitchTxns.length - 1]?.id

  const sequence =
    gameStore.transactions.length > 0
      ? Math.max(...gameStore.transactions.map((t) => t.sequence)) + 1
      : 1

  const newTxn: ScoutingTransaction = {
    id: Date.now().toString(),
    gameId: gameStore.activeGameSummary.gameId,
    timestamp: Date.now(),
    sequence,
    plateAppearanceId: currentPA?.id || 'manual_scout',
    context: 'SCORER',
    inning: currentPA?.inning || gameStore.activeGameSummary.currentInning,
    halfInning: currentPA?.halfInning || gameStore.activeGameSummary.currentHalfInning,
    batterId: currentPA?.batterId || null,
    pitcherId: currentPA?.pitcherId || null,
    isIncomplete: false,
    notes: null,
    activeTransactionFlag: true,
    eventType: 'SCOUTING',
    targetPlayerIds,
    tagValue: tagDef?.value || selectedTag.value,
    tagCategory: selectedCategory.value, // explicit coach choice, not the tag's baked-in default

    ...(selectedPositions.value.length > 0 && { targetPositions: [...selectedPositions.value] }),
    ...(activePitchId && { linkedPitchId: String(activePitchId) }),
    ...(customNote.value.trim() !== '' && { customNote: customNote.value.trim() }),
  }

  gameStore.transactions.push(newTxn)
  try {
    await db.transactions.put(JSON.parse(JSON.stringify(newTxn)))
    $q.notify({
      message: 'Scouting Evaluation Saved!',
      color: 'positive',
      icon: 'check_circle',
      position: 'top',
    })
  } catch (err) {
    console.error('Failed to save scouting eval', err)
  }

  clearSelection()
}
</script>

<style scoped>
.canvas-container {
  border: none;
  box-sizing: border-box;
  min-height: 200px;
}
</style>
